<!-- client\src\components\Navigation\Sidebar.vue -->
<template>
  <v-navigation-drawer
    v-if="authStore.user"
    v-model="drawer"
    @update:modelValue="onDrawerChange"
    app
    width="275"
    class="dark-background light-text"
    expand-on-hover
    :temporary="$vuetify.display.xs || $vuetify.display.sm"
    :permanent="$vuetify.display.mdAndUp"
    theme="dark"
  >
    <v-list nav dense>
      <v-list-item prepend-icon="mdi-home" title="Dashboard" value="dashboard" to="/dashboard" />
      <template v-for="aquarium in aquariums" :key="aquarium.id">
        <v-list-item prepend-icon="mdi-fishbowl" :title="aquarium.name" :value="aquarium.id" :to="`/aquarium/${aquarium.id}`" />
      </template>
      <v-list-item prepend-icon="mdi-plus-circle" title="Add New Aquarium" value="add_aquarium" @click="handleAddNewAquariumClick">
        <template #append>
          <PremiumFeatureIcon message="Adding more than one aquarium is a premium feature you have access to now, but in the future it may require a premium account." />
        </template>
      </v-list-item>
      
      <template v-if="authStore.user.role === 'admin'">
        <v-divider />
        <v-list-item prepend-icon="mdi-account-multiple" title="User Management" value="user_management" to="/admin/users" />
        <v-list-item prepend-icon="mdi-database" title="Aquarium Management" value="aquarium_management" to="/admin/aquariums" />
        <v-list-item prepend-icon="mdi-database" title="Log Management" value="log_management" to="/admin/logs" />
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/store';
import { useModalStore } from '@/store';
import { useSidebarStore } from '@/store';
import { usePopupStore } from '@/store';
import { ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import NewAquariumModal from '../NewAquariumModal.vue';
import PremiumFeatureIcon from '@/components/PremiumFeatureIcon.vue';

export default {
  name: 'Sidebar',
  components: {
    NewAquariumModal,
    PremiumFeatureIcon,
  },
  setup() {
    const authStore = useAuthStore();
    const sidebarStore = useSidebarStore();
    const modalStore = useModalStore();
    const popupStore = usePopupStore();
    const drawer = ref(!sidebarStore.isCollapsed);
    const aquariums = ref([]);
    const { name } = useDisplay(); // Vuetify's breakpoint system

    // Collapse sidebar on xs and sm widths
    watch(name, (val) => {
      if (val === 'xs' || val === 'sm') {
        sidebarStore.collapseSidebar();
      } else {
        sidebarStore.expandSidebar();
      }
    }, { immediate: true });

    watch(() => sidebarStore.isCollapsed, (val) => {
      drawer.value = !val;
    });

    const fetchAquariums = async () => {
      if (authStore.user) {
        try {
          const response = await axios.get('/api/aquariums/');
          aquariums.value = response.data;
        } catch (error) {
          popupStore.setMessage('Sidebar encountered an error fetching aquariums', error.message);
        }
      } else {
        aquariums.value = [];
      }
    };

    watch(() => authStore.user, async (newUser) => {
      await fetchAquariums();
    }, { immediate: true });

    function onDrawerChange(value) {
      if (value) {
        sidebarStore.expandSidebar();
      } else {
        sidebarStore.collapseSidebar();
      }
    }

    return { 
      authStore,
      modalStore,
      sidebarStore,
      popupStore,
      drawer,
      aquariums,
      onDrawerChange,
      fetchAquariums,
    };
  },
  methods: {
    handleAddNewAquariumClick() {
      this.modalStore.openNewAquariumModal({
        mode: 'user',
        verb: 'create',
        prefilledData: { 
          name: '',
          user_id: this.authStore.user.id,
        }
      });
    },
  },
  mounted() {
    this.$eventBus.on('aquarium-updated', this.fetchAquariums);
  },
  beforeUnmount() {
    this.$eventBus.off('aquarium-updated', this.fetchAquariums);
  },
};
</script>

<style scoped>
a:hover {
  text-decoration: none;
}
</style>